import React, { useCallback, useState, useEffect } from "react";
import { Box, Button, Container, Typography } from "@mui/material";
import SendIcon from "@mui/icons-material/Send";
import { NewCampaignForm } from "../common/send-action/NewCampaignForm";
import TemplatePickerDialog from "../common/send-action/TemplatePickerDialog";
import { TemplateContentPreview } from "../common/send-action/TemplateContentPreview";
import { UploadRecipientSet } from "../common/send-action/UploadRecipientSet";
import type { IZnsTemplate } from "modules/outbound-message/types/znsTemplate";
import {
  ZnsCampaignType,
  ZnsCampaignTargetAudience,
  ZnsCampaignStatus,
  ZnsSendingMode,
} from "modules/outbound-message/types/znsCampaign";
import { useAppDispatch, useAppSelector } from "app/hooks";
import {
  createZnsCampaign,
  resetCreateStatus,
  selectZnsCampaignCreateLoading,
  selectZnsCampaignCreateSuccess,
  selectZnsCampaignCreateError,
} from "../store/znsCampaignSlice";
import ZnsCampaignService from "../services/znsCampaignService";
import { toastEmitter } from "utils/toast.util";

/**
 * Manual send page: create campaign, choose template via picker dialog, set recipients.
 */
const ManualSendPage = () => {
  const dispatch = useAppDispatch();
  const isSubmitting = useAppSelector(selectZnsCampaignCreateLoading);
  const createSuccess = useAppSelector(selectZnsCampaignCreateSuccess);
  const createError = useAppSelector(selectZnsCampaignCreateError);

  const [templateDialogOpen, setTemplateDialogOpen] = useState(false);
  const [selectedTemplate, setSelectedTemplate] = useState<IZnsTemplate | null>(
    null,
  );
  const [campaignName, setCampaignName] = useState("");
  const [recipientFilePath, setRecipientFilePath] = useState<string | null>(
    null,
  );
  const resetUploadItemsRef = React.useRef<(() => void) | null>(null);

  useEffect(() => {
    if (createSuccess) {
      toastEmitter.show(
        "Campaign created and notifications generated successfully!",
        "success",
      );
      setCampaignName("");
      setSelectedTemplate(null);
      setRecipientFilePath(null);
      if (resetUploadItemsRef.current) {
        resetUploadItemsRef.current();
      }
      dispatch(resetCreateStatus());
    }
    if (createError) {
      toastEmitter.show(
        createError.Message || "Failed to create campaign.",
        "error",
      );
      dispatch(resetCreateStatus());
    }
  }, [createSuccess, createError, dispatch]);

  const hanldeUploadFile = useCallback(
    async (file: File) => {
      if (!selectedTemplate?.id) {
        toastEmitter.show(
          "Please select a template before uploading recipients.",
          "warning",
        );
        return;
      }
      const result = await ZnsCampaignService.uploadRecipients(
        selectedTemplate.id,
        file,
      );

      setRecipientFilePath(result.filePath);
    },
    [selectedTemplate],
  );

  const handleSelectTemplate = useCallback((template: IZnsTemplate) => {
    setSelectedTemplate(template);
  }, []);

  const handleSendZns = useCallback(() => {
    if (!selectedTemplate || !campaignName) {
      toastEmitter.show(
        "Please enter campaign name and select a template",
        "warning",
      );
      return;
    }
    if (!recipientFilePath) {
      toastEmitter.show(
        "Please upload a recipient file before sending.",
        "warning",
      );
      return;
    }
    dispatch(
      createZnsCampaign({
        name: campaignName,
        description: "",
        znsTemplateId: selectedTemplate.id || "",
        type: ZnsCampaignType.MANUAL,
        targetAudience: ZnsCampaignTargetAudience.CUSTOMER,
        status: ZnsCampaignStatus.ACTIVE,
        znsConfiguration: {
          recipientFilePath: recipientFilePath ?? undefined,
          sendingMode: ZnsSendingMode.IMMEDIATE,
        },
      }),
    );
  }, [campaignName, selectedTemplate, recipientFilePath, dispatch]);

  return (
    <Container
      maxWidth={false}
      sx={{
        display: "flex",
        flexDirection: "column",
        gap: 3,
        alignItems: "flex-start",
      }}
    >
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          width: "100%",
          gap: 3,
        }}
      >
        <Box
          sx={{ width: "100%", display: "flex", justifyContent: "flex-end" }}
        >
          <Button
            variant="contained"
            color="primary"
            sx={{ fontSize: 15 }}
            onClick={handleSendZns}
            disabled={isSubmitting}
          >
            <SendIcon sx={{ fontSize: 18 }} /> &nbsp;
            {isSubmitting ? "Sending..." : "Send ZNS"}
          </Button>
        </Box>
        <Box sx={{ width: "50%" }}>
          <NewCampaignForm
            selectedTemplate={selectedTemplate}
            onSelectTemplateClick={() => setTemplateDialogOpen(true)}
            campaignName={campaignName}
            onCampaignNameChange={setCampaignName}
          />
        </Box>
      </Box>
      <Box
        sx={{
          width: "100%",
          gap: 1.5,
          flexDirection: "column",
          display: "flex",
        }}
      >
        <Typography variant="h6">Recipient Set-up</Typography>
        <TemplateContentPreview
          parameters={selectedTemplate?.parameters || []}
          templateId={selectedTemplate?.id}
        />
        <UploadRecipientSet
          uploadFile={hanldeUploadFile}
          disabled={!selectedTemplate}
          onReset={(resetFn) => (resetUploadItemsRef.current = resetFn)}
        />
      </Box>
      <TemplatePickerDialog
        open={templateDialogOpen}
        onClose={() => setTemplateDialogOpen(false)}
        onSelectTemplate={handleSelectTemplate}
        selectedTemplate={selectedTemplate}
      />
    </Container>
  );
};

export default React.memo(ManualSendPage);
